/**
 * XR — WebXR / OpenXR support.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * WHAT THIS IS
 *
 * WebXR is the browser's binding to the platform's OpenXR runtime: on a Quest,
 * SteamVR, WMR or Pico headset the browser is an OpenXR application and this
 * subsystem drives it. There is no separate "OpenXR mode" to add — an
 * `immersive-vr` session IS the OpenXR path, and a headset that speaks OpenXR
 * is reachable from here without any native code.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * THE THREE THINGS THAT MAKE A FLAT SHOOTER WORK IN A HEADSET
 *
 * 1. THE CAMERA STOPS BEING THE PLAYER'S HEAD.
 *    Flat, `player` composes an eye transform every frame — bob, roll, landing
 *    dip, recoil, trauma shake — and writes it onto `ctx.camera`. In VR the
 *    headset owns that transform absolutely; anything the game adds to it is
 *    motion the player's inner ear did not authorise. So the composition still
 *    runs (the weapon reads its springs for counter-motion) but never reaches
 *    the camera: `player.xrActive` suppresses the write, and instead the
 *    play-space rig is placed on the movement machine's capsule, with the HMD
 *    pose supplying head position and orientation on top of it.
 *
 * 2. THE WEAPON STOPS BEING A VIEWMODEL.
 *    Flat, the weapon is drawn in a separate scene by a separate camera so it
 *    can never clip a wall, and it is welded to the view axis. Neither survives
 *    stereo: the gun is a real object an arm's length away, held in a hand that
 *    points wherever the player points it. `viewmodel.trackCamera` is turned
 *    off and the anchor is driven from the right controller's grip pose
 *    instead, and `weapons.boreAim` makes rounds leave the barrel along the
 *    barrel rather than along the view axis.
 *
 * 3. THE HUD STOPS BEING DOM.
 *    A WebXR session does not composite the page into the headset. The DOM HUD
 *    is hidden and a wrist panel (`hud.js`) carries health and ammo.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * LOCOMOTION AND COMFORT
 *
 * Stick locomotion runs through the EXISTING movement state machine rather than
 * moving the rig directly: the left stick is written into `input.stick` during
 * the input latch, so VR walking inherits the swept capsule, step-up, slopes,
 * crouch, sprint and every collision guarantee the flat game has. Turning is
 * snapped (45 degrees by default), because smooth yaw is the single most
 * reliable way to make people ill; `?vrturn=smooth` opts into the other one.
 *
 * Room-scale movement is reconciled against the capsule every frame: the
 * physical head displacement is offered to `checkCapsule`, applied if the
 * destination is free, and refused if it is not — so leaning through a wall
 * pushes back instead of letting the player's viewpoint into solid geometry.
 * The comfort vignette closes during artificial locomotion and during any frame
 * where that push-back is doing work.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * THE FRAME LOOP
 *
 * An XR session is driven by `XRSession.requestAnimationFrame`, not the
 * window's. `renderer.setAnimationLoop` is handed the engine's own step, three
 * routes it to whichever loop is live, and `Engine.start`'s rAF is retired.
 * That handoff is permanent once VR has been entered: three swaps the loop back
 * to rAF on session end by itself, so the flat game keeps running through the
 * same callback afterwards.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * PUBLIC API — `const xr = ctx.get('xr')`
 *   xr.supported        boolean, resolved once at init
 *   xr.presenting       boolean
 *   xr.enter()          request a session (must be called from a user gesture)
 *   xr.exit()           end it
 *   xr.turnStyle        'snap' | 'smooth'
 *
 * EVENTS EMITTED
 *   xr:session   { presenting: boolean }
 */

import * as THREE from 'three';
import { XrControls, deadzone } from './controls.js';
import { ComfortVignette } from './comfort.js';
import { WristHud } from './hud.js';
import { createVrButton } from './button.js';

const SNAP_ANGLE = Math.PI / 4; // 45 degrees
const SMOOTH_TURN_RATE = 2.2; // rad/s
const TURN_ENGAGE = 0.72; // stick deflection that arms a snap
const TURN_RELEASE = 0.35; // and the one that re-arms it
const MOVE_DEADZONE = 0.18;

/** Eye height the game considers "standing", used to offset stance changes. */
const REFERENCE_STAND_EYE = 1.66;

const UP = new THREE.Vector3(0, 1, 0);
/**
 * Grip space -> weapon axis. `xr-standard` grip space points -Z out of the top
 * of the handle and +Y up the shaft, which is roughly 40 degrees off where a
 * hand wrapped round a pistol grip actually points a barrel.
 */
const GRIP_TILT = new THREE.Quaternion().setFromEuler(
  new THREE.Euler(-Math.PI * 0.22, 0, 0, 'XYZ')
);

export class XrSystem {
  static id = 'xr';
  static deps = ['render', 'player', 'weapons'];

  constructor() {
    this.supported = false;
    this.presenting = false;
    this.session = null;

    this.turnStyle = 'snap';
    /** Accumulated artificial yaw. The HMD's own yaw is added on top of it. */
    this.turnYaw = 0;

    this.controls = new XrControls();
    this.comfort = null;
    this.hud = null;

    this.rig = null;
    this.gripL = null;
    this.gripR = null;
    this.rayL = null;
    this.rayR = null;

    this._turnArmed = true;
    this._lastHeadYaw = 0;
    this._standEye = REFERENCE_STAND_EYE;
    this._headLocal = new THREE.Vector3();
    this._prevHeadLocal = new THREE.Vector3();
    this._hasHead = false;
    this._headOffsetWorld = new THREE.Vector3();
    this._blocked = 0;

    this._q = new THREE.Quaternion();
    this._v = new THREE.Vector3();
    this._v2 = new THREE.Vector3();
    this._e = new THREE.Euler(0, 0, 0, 'YXZ');

    this._hudState = {
      hp: 100, maxHp: 100, ammo: 0, reserve: 0, magSize: 0, name: '', dead: false,
    };
    this._sessionPayload = { presenting: false };
    this._poll = (input) => this._pollXr(input);
    this._button = null;
    this._loopInstalled = false;
  }

  /* ==================================================================== */
  /* init                                                                 */
  /* ==================================================================== */

  async init(ctx) {
    this.ctx = ctx;
    this.render = ctx.get('render');
    this.player = ctx.get('player');
    this.weapons = ctx.get('weapons');
    this.renderer = this.render.renderer;

    const params = new URLSearchParams(location.search);
    if (params.get('vrturn') === 'smooth') this.turnStyle = 'smooth';

    // The play-space rig. The XR reference space is `local-floor`, so this
    // group's origin is the floor the player is standing on and everything the
    // runtime reports — head, both controllers — is expressed inside it.
    this.rig = new THREE.Group();
    this.rig.name = 'xr-rig';
    this.rig.matrixAutoUpdate = true;
    ctx.scene.add(this.rig);

    this.comfort = new ComfortVignette();
    this.hud = new WristHud();

    // Capture mode has no headset and a stray "ENTER VR" button in the corner
    // would be in every screenshot.
    if (ctx.config.deterministic) {
      console.info('[xr] deterministic capture — VR entry point suppressed');
      return;
    }

    if (!navigator.xr || typeof navigator.xr.isSessionSupported !== 'function') {
      console.info('[xr] WebXR unavailable in this browser');
      return;
    }
    // Secure context only: WebXR is gated on HTTPS (or localhost), which is why
    // the deploy target is a TLS host rather than a plain file server.
    this.supported = await navigator.xr.isSessionSupported('immersive-vr').catch(() => false);
    console.info(`[xr] immersive-vr ${this.supported ? 'supported' : 'not supported'}`);
    if (!this.supported) return;

    this._button = createVrButton({
      onEnter: () => this.enter(),
      onExit: () => this.exit(),
    });
  }

  /* ==================================================================== */
  /* session lifecycle                                                    */
  /* ==================================================================== */

  async enter() {
    if (this.presenting || !this.supported) return;
    let session;
    try {
      session = await navigator.xr.requestSession('immersive-vr', {
        // `local-floor` is required, not optional: the whole rig placement
        // assumes the runtime reports head height above a known floor. Without
        // it the player would spawn with their eyes at their ankles.
        requiredFeatures: ['local-floor'],
        optionalFeatures: ['bounded-floor', 'layers'],
      });
    } catch (err) {
      console.warn('[xr] session request refused', err);
      this._button?.setError('headset refused the session');
      return;
    }
    const renderer = this.renderer;
    renderer.xr.enabled = true;
    // The stereo path calls updateCamera itself, before it needs the eye
    // cameras — see RenderSystem.renderXR.
    renderer.xr.cameraAutoUpdate = false;
    renderer.xr.setReferenceSpaceType('local-floor');
    // Fixed-foveated rendering. The periphery of a headset lens is well past
    // the resolving power of the eye behind it, and this is free headroom.
    renderer.xr.setFoveation(0.35);
    await renderer.xr.setSession(session);

    this.session = session;
    session.addEventListener('end', () => this._onEnd(), { once: true });
    this._onStart();
  }

  async exit() {
    await this.session?.end().catch(() => {});
  }

  _onStart() {
    const ctx = this.ctx;
    this.presenting = true;

    // Hand the engine's step to three: it routes it to the XRSession's frame
    // callback now and back to rAF when the session ends. The window rAF loop
    // Engine.start owns is retired for good, so there is never a frame driven
    // from both.
    if (!this._loopInstalled) {
      ctx.engine.stop();
      this.renderer.setAnimationLoop((t) => ctx.engine.step(t));
      this._loopInstalled = true;
    }

    // The camera becomes a child of the rig. three's updateCamera resolves the
    // headset pose against `camera.parent`, so this is what makes the rig mean
    // anything at all.
    this._prevCameraParent = ctx.camera.parent;
    this.rig.add(ctx.camera);
    ctx.camera.add(this.comfort.mesh);

    // Controller spaces are reported in the reference space, so they hang off
    // the rig too.
    const xr = this.renderer.xr;
    this.gripL = xr.getControllerGrip(0);
    this.gripR = xr.getControllerGrip(1);
    this.rayL = xr.getController(0);
    this.rayR = xr.getController(1);
    this.rig.add(this.gripL, this.gripR, this.rayL, this.rayR);
    this.gripL.add(this.hud.mesh);

    // Both grips are index-addressed, but the runtime decides which index is
    // which hand. Track handedness per frame instead of trusting the order.
    this._gripHand = ['left', 'right'];

    this.turnYaw = this.player.yaw;
    this._standEye = this.player.eyeHeight || REFERENCE_STAND_EYE;
    this._hasHead = false;
    this._blocked = 0;

    this.player.xrActive = true;
    this.weapons.boreAim = true;
    const vm = this.weapons.viewmodel;
    if (vm) {
      this._vmTrackSaved = vm.trackCamera;
      vm.trackCamera = false;
    }

    const ui = ctx.peek('ui');
    if (ui?.root) {
      this._uiDisplaySaved = ui.root.style.display;
      ui.root.style.display = 'none';
    }

    ctx.input.auxPoll = this._poll;
    ctx.input.requestPointerLock = () => {}; // no cursor to lock in a headset
    this._button?.setPresenting(true);

    this._sessionPayload.presenting = true;
    ctx.events.emit('xr:session', this._sessionPayload);
    console.info('[xr] session started');
  }

  _onEnd() {
    const ctx = this.ctx;
    this.presenting = false;
    this.session = null;

    ctx.camera.remove(this.comfort.mesh);
    this.rig.remove(ctx.camera);
    if (this._prevCameraParent) this._prevCameraParent.add(ctx.camera);
    if (this.gripL) this.gripL.remove(this.hud.mesh);
    if (this.gripL) this.rig.remove(this.gripL, this.gripR, this.rayL, this.rayR);

    this.player.xrActive = false;
    this.weapons.boreAim = false;
    const vm = this.weapons.viewmodel;
    if (vm) vm.trackCamera = this._vmTrackSaved ?? true;

    const ui = ctx.peek('ui');
    if (ui?.root) ui.root.style.display = this._uiDisplaySaved ?? '';

    ctx.input.auxPoll = null;
    delete ctx.input.requestPointerLock; // fall back to the prototype's

    // Hand the flat camera the yaw the player was actually facing, so leaving
    // VR does not spin them around.
    this.player.movement.yaw = this._lastHeadYaw;
    this.player.movement.pitch = 0;

    // The internal chain is still sized for one eye; the canvas is back.
    ctx.engine.resize();
    this._button?.setPresenting(false);

    this._sessionPayload.presenting = false;
    ctx.events.emit('xr:session', this._sessionPayload);
    console.info('[xr] session ended');
  }

  /* ==================================================================== */
  /* input latch — runs inside Input.beginFrame, before any fixedUpdate    */
  /* ==================================================================== */

  _pollXr(input) {
    if (!this.presenting || !this.session) return;
    const ctx = this.ctx;
    const dt = ctx.time.dt || 1 / 90;

    this.controls.poll(this.session);
    const L = this.controls.left;
    const R = this.controls.right;

    // ---- turning ---------------------------------------------------------
    // Applied here rather than in update() so the yaw the movement machine
    // latches this frame is the yaw the player will actually be facing.
    const turnAxis = R.present ? R.x : 0;
    if (this.turnStyle === 'smooth') {
      this.turnYaw -= deadzone(turnAxis, MOVE_DEADZONE) * SMOOTH_TURN_RATE * dt;
    } else {
      if (Math.abs(turnAxis) < TURN_RELEASE) this._turnArmed = true;
      else if (this._turnArmed && Math.abs(turnAxis) > TURN_ENGAGE) {
        this._turnArmed = false;
        this.turnYaw -= Math.sign(turnAxis) * SNAP_ANGLE;
        // A snap is an instantaneous view change with no optical flow at all,
        // which is exactly why it is comfortable — but the head pose has just
        // moved a long way inside the rig, and the room-scale reconciliation
        // would read that as the player physically teleporting. Re-seed it.
        this._hasHead = false;
      }
    }
    this.rig.rotation.y = this.turnYaw;
    this.rig.updateMatrixWorld(true);

    // ---- head pose -------------------------------------------------------
    // updateCamera writes the current headset pose onto ctx.camera relative to
    // the rig. Calling it here as well as in the renderer costs nothing and
    // means locomotion is head-relative with zero frames of lag.
    this.renderer.xr.updateCamera(ctx.camera);
    this._e.setFromQuaternion(ctx.camera.getWorldQuaternion(this._q), 'YXZ');
    const headYaw = this._e.y;

    // The movement machine's yaw IS the locomotion basis. Pointing it at the
    // head is what makes "forward" mean "where I am looking", which is the
    // behaviour every VR shooter ships by default.
    const m = this.player.movement;
    m.yaw = headYaw;
    m.pitch = 0;
    m.yawRate = 0;
    this._lastHeadYaw = headYaw;

    // ---- locomotion ------------------------------------------------------
    // Into `input.stick`, so it merges with the existing move vector and runs
    // through the same state machine (and therefore the same collision) as WASD.
    if (L.present) {
      input.stick.moveX = deadzone(L.x, MOVE_DEADZONE);
      // Input negates moveY for the pad convention: stick forward is -1.
      input.stick.moveY = deadzone(L.y, MOVE_DEADZONE);
    } else {
      input.stick.moveX = 0;
      input.stick.moveY = 0;
    }
    input.stick.lookX = 0;
    input.stick.lookY = 0;

    // ---- buttons ---------------------------------------------------------
    // Mapped onto the codes the flat game already binds, so every consumer —
    // the movement machine, the weapon, the pause menu — is untouched.
    input.setVirtual('Mouse0', R.trigger);              // fire
    input.setVirtual('Space', R.primary);               // A -> jump
    input.setVirtual('ControlLeft', R.secondary);       // B -> crouch
    input.setVirtual('ShiftLeft', L.squeeze);           // left grip -> sprint
    input.setVirtual('KeyR', L.primary);                // X -> reload
    input.setVirtual('Tab', L.secondary);               // Y -> swap weapon
    input.setVirtual('KeyF', L.trigger);                // left trigger -> use
    // ADS is physical in VR: the player brings the sights to their eye. Never
    // synthesise the hold, or the weapon would snap to a view-space ADS pose
    // and fight the hand holding it.
    input.setVirtual('Mouse2', false);
  }

  /* ==================================================================== */
  /* frame — after player.update, before every lateUpdate                 */
  /* ==================================================================== */

  update(dt, ctx) {
    if (!this.presenting) return;

    this._placeRig(dt);
    // Re-derive the camera against the rig's final transform. player.update ran
    // between the latch and here; this is the pose the renderer, the weapon and
    // everything reading ctx.camera in lateUpdate will see.
    this.renderer.xr.updateCamera(ctx.camera);

    this._driveWeapon();
    this._updateHud();

    // ---- comfort ---------------------------------------------------------
    // Artificial translation is the trigger, not speed as such: a player who
    // physically walks generates the same optical flow with full vestibular
    // agreement and needs no tunnel at all.
    const stick = Math.min(1, Math.hypot(ctx.input.stick.moveX, ctx.input.stick.moveY));
    const speedFrac = Math.min(1, this.player.horizontalSpeed / 5.5);
    let tunnel = Math.min(1, stick * 0.55 + speedFrac * 0.55);
    if (this.turnStyle === 'smooth') {
      tunnel = Math.max(tunnel, Math.min(1, Math.abs(deadzone(this.controls.right.x, MOVE_DEADZONE))));
    }
    // Being pushed out of a wall is unrequested translation by definition.
    tunnel = Math.max(tunnel, this._blocked);
    this._blocked = Math.max(0, this._blocked - dt * 3);
    if (!this.player.grounded) tunnel = Math.max(tunnel, 0.5);
    this.comfort.update(dt, tunnel);
  }

  /**
   * Put the play space where the movement capsule is, and reconcile whatever
   * the player did with their actual body against it.
   */
  _placeRig(dt) {
    const ctx = this.ctx;
    const player = this.player;
    const m = player.movement;
    const feet = m.renderPosition;

    // Head position inside the rig. `camera.position` IS the local pose,
    // because the camera is a direct child of the rig.
    this._headLocal.copy(ctx.camera.position);

    if (!this._hasHead) {
      this._prevHeadLocal.copy(this._headLocal);
      this._hasHead = true;
    }

    // ---- room-scale reconciliation ---------------------------------------
    // Offer the physical head displacement to the capsule. If the destination
    // is free the capsule follows the body; if it is not, the capsule stays and
    // the rig placement below quietly pushes the view back out. That push-back
    // is the only honest option — the alternative is a viewpoint inside a wall,
    // which breaks the depth buffer, the occlusion and the player's trust in
    // the geometry all at once.
    const dxLocal = this._headLocal.x - this._prevHeadLocal.x;
    const dzLocal = this._headLocal.z - this._prevHeadLocal.z;
    if (dxLocal * dxLocal + dzLocal * dzLocal > 1e-8) {
      const c = Math.cos(this.turnYaw);
      const s = Math.sin(this.turnYaw);
      // Rig yaw is the only rotation on the rig, so this is its 2D basis.
      const dxWorld = dxLocal * c + dzLocal * s;
      const dzWorld = -dxLocal * s + dzLocal * c;
      const cc = m.character;
      if (cc) {
        const wantX = cc.position.x + dxWorld;
        const wantZ = cc.position.z + dzWorld;
        if (cc.checkCapsule(wantX, cc.position.y, wantZ, cc.height)) {
          cc.setPosition(wantX, cc.position.y, wantZ);
          // Keep the interpolation history with it, or the render position
          // lerps back toward where the capsule used to be and the whole world
          // shears for a frame.
          m.position.set(cc.position.x, cc.position.y, cc.position.z);
          m.prevPosition.x += dxWorld;
          m.prevPosition.z += dzWorld;
          m.renderPosition.x += dxWorld;
          m.renderPosition.z += dzWorld;
        } else {
          this._blocked = 1;
        }
      }
    }
    this._prevHeadLocal.copy(this._headLocal);

    // ---- place the rig ----------------------------------------------------
    // Vertically the rig origin is the floor under the capsule, offset by
    // whatever the game says the stance has done to eye height — so pressing
    // crouch lowers the player by the same amount it would flat, on top of
    // however tall they physically are.
    const stanceDrop = (player.eyeHeight || this._standEye) - this._standEye;

    // Horizontally the head is pinned over the capsule. When the reconciliation
    // above succeeded, the capsule moved by exactly the head's displacement and
    // this cancels out to no artificial translation at all.
    this._headOffsetWorld.set(this._headLocal.x, 0, this._headLocal.z);
    this._headOffsetWorld.applyAxisAngle(UP, this.turnYaw);

    this.rig.position.set(
      feet.x - this._headOffsetWorld.x,
      feet.y + stanceDrop,
      feet.z - this._headOffsetWorld.z
    );
    this.rig.rotation.y = this.turnYaw;
    this.rig.updateMatrixWorld(true);
  }

  /**
   * Hang the weapon off the right hand.
   *
   * The viewmodel rig poses everything relative to `anchor`, and its hip pose
   * puts the weapon at an offset from that anchor (down and to the right of the
   * eye, where a rifle sits when it is not shouldered). Placing the anchor AT
   * the grip would therefore put the gun a forearm's length past the hand. The
   * anchor is offset backwards by whatever the pose stack produced last frame
   * instead, which lands the weapon body in the hand and — because it is
   * subtracted rather than zeroed — leaves recoil, reload and inspect animation
   * playing normally around it.
   */
  _driveWeapon() {
    const vm = this.weapons.viewmodel;
    if (!vm) return;
    const grip = this._gripFor('right');
    if (!grip) return;

    grip.updateMatrixWorld(true);
    this._q.setFromRotationMatrix(grip.matrixWorld);
    // A held rifle sits along the forearm, not along the controller's grip
    // axis: xr-standard grip space points -Z out of the top of the handle and
    // +Y along it, which is about 40 degrees off where a trigger hand actually
    // points a barrel.
    this._q.multiply(GRIP_TILT);

    vm.anchor.quaternion.copy(this._q);
    this._v.copy(vm.rig.position).applyQuaternion(this._q);
    vm.anchor.position.setFromMatrixPosition(grip.matrixWorld).sub(this._v);
    vm.anchor.updateMatrixWorld(true);

    // The pose stack must not try to shoulder the weapon: ADS in VR is the
    // player physically raising it, and a view-space ADS solve would drag the
    // gun off the hand and onto the camera axis.
    const st = this.weapons._state;
    if (st) {
      st.ads = false;
      st.sprint = false;
      st.lowReady = false;
    }
  }

  _gripFor(handedness) {
    const session = this.session;
    if (!session) return null;
    const sources = session.inputSources;
    if (!sources) return null;
    // inputSources is index-aligned with the controller groups three built.
    for (let i = 0; i < sources.length && i < 2; i++) {
      if (sources[i].handedness !== handedness) continue;
      return i === 0 ? this.gripL : this.gripR;
    }
    return null;
  }

  _updateHud() {
    // Keep the panel on whichever grip is currently the left hand.
    const left = this._gripFor('left');
    if (left && this.hud.mesh.parent !== left) left.add(this.hud.mesh);

    const s = this._hudState;
    const p = this.player;
    // Both of these are preallocated snapshots — `weapons.ammo` builds a fresh
    // object on every read, which would be a per-frame allocation.
    const w = this.weapons.getHudState();
    s.hp = p.health?.value ?? 0;
    s.maxHp = p.maxHealth ?? 100;
    s.dead = p.dead === true;
    s.ammo = w.ammo;
    s.reserve = w.reserve;
    s.magSize = w.magSize;
    s.name = w.name ?? '';
    this.hud.sync(s);
  }

  dispose() {
    if (this.presenting) this.session?.end().catch(() => {});
    this.comfort?.dispose();
    this.hud?.dispose();
    this.rig?.parent?.remove(this.rig);
    this._button?.dispose();
    if (this.ctx?.input) this.ctx.input.auxPoll = null;
  }
}
